import { FC } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";

import { DisplayState } from "@/constants/state";
import { WithTestID } from "apps/website/types";
import {
  Size,
  sizeClassMap,
} from "apps/website/components/base/Button/Button.map";

import Icon from "../Icon/Icon";
import TextSubtitle from "../Text/TextSubtitle/TextSubtitle";
import ButtonState from "../Button/ButtonState";
import { LoadingSpinner } from "../../form/LoadingSpinner/LoadingSpinner";
import { TextSubtitleVariantsProps } from "../Text/TextSubtitle/styles";

import {
  TColor,
  designClassMap,
  TIconName,
  TDesign,
} from "./CTAButton.map";

interface IIconProp {
  name: TIconName
  position?: "trailing" | "leading"
  ariaHidden?: boolean;
}

interface ICTABase extends WithTestID {
  color?: TColor;
  disabled?: boolean;
  size?: Size;
  icon?: IIconProp
  text: string
  component?: string;
  ariaLabel?: string
  design?: TDesign
  textSize?: TextSubtitleVariantsProps["size"];
}

type TButtonCTA = ICTABase & React.ButtonHTMLAttributes<HTMLButtonElement> & {
  state?: DisplayState;
};

type TLinkCTA = ICTABase & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export type TCTAButtonProps = TButtonCTA | TLinkCTA;

function isButton(obj: TButtonCTA | TLinkCTA): obj is TButtonCTA {
  return (typeof obj === "object" && obj !== null && "onClick" in obj && typeof (obj).onClick === "function");
}

const Content = ({ icon, text, textSize = 2 }: Pick<ICTABase, "icon" | "text" | "textSize">) => (

  <div className="flex justify-center items-center w-full max-h-fit">
    <div className="inline-flex items-center gap-2">
      { icon?.position === "leading" && (
        <Icon size="xsmall" icon={icon.name} ariaHidden={icon.ariaHidden} color={"inherit"} />
      ) }

      <TextSubtitle size={textSize}>{ text }</TextSubtitle>

      { icon?.position === "trailing" && (
        <Icon size="xsmall" icon={icon.name} ariaHidden={icon.ariaHidden} color={"inherit"} />
      ) }
    </div>
  </div>

);

export const CTAButton: FC<TCTAButtonProps> = (props) => {
  const {
    color = "primary",
    disabled,
    size = "default",
    design = "default",
    icon,
    text,
    textSize = 2,
    component = "CTAButton",
    ariaLabel,
  } = props;

  const t = useTranslations("General.UX");

  const isLoading = isButton(props) && props.state === DisplayState.LOADING;
  const computedClasses = `${designClassMap[design].base} ${designClassMap[design][color]} ${sizeClassMap[size]} ${isLoading ? "cursor-not-allowed" : ""}`;

  const commonProps = {
    "data-component": component,
    className: computedClasses,
    "aria-label": ariaLabel,
  };

  const content = <Content icon={icon} text={text} textSize={textSize} />;

  const customStateContent = {
    [DisplayState.ERROR]: <TextSubtitle size={2}>{ t("somethingWentWrong") }</TextSubtitle>,
    [DisplayState.LOADING]: <LoadingSpinner size={24} color="white" />,
  };

  if (isButton(props)) {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      props.onClick?.(e);
    };

    return (
      <button
        data-testid="cta-button"
        type={props.type ?? "button"}
        onClick={handleClick}
        disabled={disabled}
        {...commonProps }
      >
        <ButtonState
          state={ props.state ?? DisplayState.READY }
          content={customStateContent ?? undefined}
        >
          { content }
        </ButtonState>
      </button>);
  }

  // Next.js Link does not support the `disabled` attribute.
  if (disabled) {
    return (
      <span
        data-testid="cta-link-disabled"
        aria-disabled="true"
        data-disabled="true"
        {...commonProps}
      >
        { content }
      </span>
    );
  }

  return (
    <Link href={props.href ?? "#"} data-testid="cta-link" {...commonProps}>
      { content }
    </Link>
  );
};
